/**
 * Authentication API Module
 *
 * This module uses the customInstance function from './axios-config'
 * which provides enhanced error handling, retry logic, and cancellation support.
 *
 * All requests automatically include:
 * - JWT Bearer token from localStorage (if available)
 * - Global 401/403 error handling with redirect to login
 * - Automatic retry logic for network/5xx errors
 * - Request cancellation support via promise.cancel()
 */

import { User } from '@/schema'

import { customInstance } from './axios-config'

// User interface defined here to avoid circular dependency
interface UserResponse {
  data: User[]
}

export interface Metadata {
  api_version: string
  request_id: string
  timestamp: string
}

export const authApi = {
  /**
   * Logs out the current user
   * @returns Promise that resolves when logout is complete
   */
  logout: async (): Promise<void> => {
    return customInstance<void>({
      method: 'post',
      url: '/auth/logout',
    })
  },

  /**
   * Fetches current authenticated user information
   * @returns Promise with current user data
   */
  getCurrentUser: async (): Promise<User> => {
    return customInstance<UserResponse>({
      method: 'get',
      url: '/admin/v1/me',
    }).then((response) => response?.data?.[0])
  },

  activateUser: async (): Promise<User> => {
    return customInstance<UserResponse>({
      method: 'post',
      url: '/admin/v1/me/activate',
    }).then((response) => response?.data?.[0])
  },
}
